import { useCallback } from 'react';

import { useNavigate, useParams } from 'react-router';
import { Link } from 'react-router-dom';

import { FontAwesomeIcon } from '@appcharge/shared-ui';
import useUsers from 'api/useUsers';
import { EPublisherSolutionModel, User } from 'common/contracts';
import { ELocalStorageKeys } from 'constants/enums';
import { localStorageUtil } from 'utils/localStorageUtil';

import {
  Sidebar,
  SidebarContent,
  SidebarMenu,
  SidebarMenuItem
} from 'components/ui/sidebar';
import { Skeleton } from 'components/ui/skeleton';

import { cn } from '../../components/lib/utils';

import { SidebarGroupComponent } from './components';
import { SidebarProvider, useSidebar } from './context';
import AppchargeIcon from './elements/AppchargeIcon';
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenuButton
} from './elements/index';
import { useProjectInfo, useSidebarNavigation } from './hooks';
import { ESideBarContext } from './types';

// Main component that wraps the sidebar with context
export function AcSidebar() {
  const { publisherId } = useParams();
  const { fetchFeatureFlags } = useUsers({ publisherId });

  // Get user details from local storage
  const userDetails = localStorageUtil.getAny<User>(
    ELocalStorageKeys.USER_DETAILS
  );

  return (
    <SidebarProvider
      initialUserDetails={userDetails}
      initialFeatureFlags={fetchFeatureFlags.data?.featureFlags}
    >
      <AcSidebarContent />
    </SidebarProvider>
  );
}

// Actual sidebar content component that uses the context
function AcSidebarContent() {
  const { context, navItems, userDetails, isLoading } = useSidebar();
  const { getProjectName, getPortalName, getPortalLevelStoreName } =
    useProjectInfo();
  const { publisherId } = useParams();
  const navigate = useNavigate();
  const projectsDetails = localStorageUtil.getAny<User>(
    ELocalStorageKeys.USER_DETAILS
  )?.projects;
  const projectItem = projectsDetails?.find(
    (item) => item.publisherId === publisherId
  );
  const isCheckout =
    projectItem?.projectType === EPublisherSolutionModel.CHECKOUT;

  const handleBack = useCallback(() => {
    const currentPortalId = userDetails?.portals?.find((portal) =>
      portal.stores.some((store) => store.storeId === publisherId)
    )?.portalId;

    if (context === ESideBarContext.PORTAL_LEVEL_STORE && currentPortalId) {
      navigate(`/portal/${currentPortalId}/settings/portal`);
    } else {
      navigate('../');
    }
  }, [context, navigate, publisherId, userDetails?.portals]);

  const getTitle = () => {
    if (context === ESideBarContext.COMPANY) return userDetails?.company;
    if (context === ESideBarContext.PROJECT) return getProjectName();
    if (context === ESideBarContext.PORTAL) return getPortalName();
    if (context === ESideBarContext.PORTAL_LEVEL_STORE)
      return getPortalLevelStoreName(publisherId);
    return '';
  };

  // Setup navigation based on URL and context
  useSidebarNavigation();

  // Create a skeleton placeholder for nav items
  const renderSkeletons = () => {
    return Array(3)
      .fill(0)
      .map((_, index) => (
        <SidebarGroup key={`skeleton-${index}`}>
          <SidebarGroupLabel>
            <Skeleton className="h-4 w-24" />
          </SidebarGroupLabel>
          <SidebarMenu>
            {Array(index + 2)
              .fill(0)
              .map((_, itemIndex) => (
                <SidebarMenuItem key={`skeleton-item-${itemIndex}`}>
                  <div className="flex items-center gap-2 py-2 px-3 w-full">
                    <Skeleton className="h-4 w-4" />
                    <Skeleton className="h-4 w-full" />
                  </div>
                </SidebarMenuItem>
              ))}
          </SidebarMenu>
        </SidebarGroup>
      ));
  };

  return (
    <Sidebar>
      <SidebarContent className="p-6 gap-y-4">
        <img
          className={cn('logo w-[111px]')}
          alt="logo"
          src="./assets/images/ac-logo-purple.svg"
        />
        <SidebarGroup>
          <SidebarGroupLabel>Account</SidebarGroupLabel>
          <SidebarMenu className="gap-0">
            {(context === ESideBarContext.PROJECT ||
              context === ESideBarContext.PORTAL ||
              context === ESideBarContext.PORTAL_LEVEL_STORE) && (
              <SidebarMenuItem>
                <SidebarMenuButton
                  onClick={handleBack}
                  className="flex items-center gap-x-2"
                >
                  <FontAwesomeIcon icon="fa-solid p-3 fa-arrow-left-to-bracket" />
                  Back
                </SidebarMenuButton>
              </SidebarMenuItem>
            )}
            <SidebarMenuItem>
              <Link to="/" title="Go to Welcome Page">
                <SidebarMenuButton>
                  <AppchargeIcon context={context} isCheckout={isCheckout} />
                  <span className="font-semibold">{getTitle()}</span>
                </SidebarMenuButton>
              </Link>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroup>

        {isLoading
          ? renderSkeletons()
          : navItems.map((group, index) => (
              <SidebarGroupComponent
                key={`${group.title}-${index}`}
                group={group}
              />
            ))}
      </SidebarContent>
    </Sidebar>
  );
}

export default AcSidebar;
